import { Injectable } from '@angular/core';

export const TOAST_LIVE_REGION_POLITE_ID = 'toast-announcer-polite';
export const TOAST_LIVE_REGION_ASSERTIVE_ID = 'toast-announcer-assertive';
export const TOAST_VISUAL_HOST_ID = 'toast-visual';

export interface ToastOptions {
  duration?: number;
  position?:
    | 'bottom-right'
    | 'center'
    | 'top-right'
    | 'top-center'
    | 'bottom-center';
  /**
   * Screen reader announcement. Default `polite` (e.g. link copied after share).
   * Use `assertive` only for urgent interruptions.
   */
  ariaLive?: 'polite' | 'assertive' | 'off';
}

const TOAST_VISIBLE_CLASS = 'toast-visual--visible';
const POSITION_CLASSES: Record<
  NonNullable<ToastOptions['position']>,
  string
> = {
  center: 'toast-visual--center',
  'top-right': 'toast-visual--top-right',
  'top-center': 'toast-visual--top-center',
  'bottom-center': 'toast-visual--bottom-center',
  'bottom-right': 'toast-visual--bottom-right',
};

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastTimeout: ReturnType<typeof setTimeout> | null = null;
  private liveRegionResetTimeout: ReturnType<typeof setTimeout> | null = null;
  private visualToastHost: HTMLDivElement | null = null;

  /**
   * Binds to the persistent live regions in index.html (present before the
   * first toast). Call once at bootstrap; safe to call repeatedly.
   */
  public initializeLiveRegions(): void {
    this.ensureLiveRegion('polite');
    this.ensureLiveRegion('assertive');
    this.ensureVisualHost();
  }

  /**
   * Shows a toast notification with the given message.
   * @param message The message to display in the toast
   * @param options Toast configuration options (duration, position, etc.)
   */
  public showToast(message: string, options?: ToastOptions | number): void {
    this.clearVisualToast();

    let duration = 3000;
    let position: ToastOptions['position'] = 'bottom-right';
    let ariaLive: NonNullable<ToastOptions['ariaLive']> = 'polite';

    if (typeof options === 'number') {
      duration = options;
    } else if (options) {
      duration = options.duration ?? duration;
      position = options.position ?? position;
      if (options.ariaLive) {
        ariaLive = options.ariaLive;
      }
    }

    if (ariaLive !== 'off') {
      const region = this.ensureLiveRegion(
        ariaLive === 'assertive' ? 'assertive' : 'polite'
      );
      if (region) {
        this.announceInLiveRegion(region, message);
      }
    }

    if (ariaLive === 'polite') {
      const visual = this.ensureVisualHost();
      if (visual) {
        this.showVisualToast(visual, message, position, duration);
      }
    }
  }

  /**
   * Announces a message to screen readers without showing visible UI.
   */
  public announce(
    message: string,
    politeness: 'polite' | 'assertive' = 'polite'
  ): void {
    const region = this.ensureLiveRegion(politeness);
    if (!region) {
      return;
    }
    this.announceInLiveRegion(region, message);
  }

  /**
   * SR-only live region — always visually hidden; never shares the gray toast UI.
   */
  private announceInLiveRegion(
    region: HTMLDivElement,
    message: string
  ): void {
    if (this.liveRegionResetTimeout) {
      clearTimeout(this.liveRegionResetTimeout);
    }

    region.textContent = '';

    this.liveRegionResetTimeout = setTimeout(() => {
      region.textContent = message;
    }, 50);
  }

  private showVisualToast(
    visual: HTMLDivElement,
    message: string,
    position: ToastOptions['position'],
    duration: number
  ): void {
    this.stripVisualPresentation(visual);
    visual.textContent = message;
    visual.classList.add(TOAST_VISIBLE_CLASS);
    if (position) {
      visual.classList.add(POSITION_CLASSES[position]);
    }
    this.visualToastHost = visual;

    this.toastTimeout = setTimeout(() => {
      this.clearVisualToast();
    }, duration);
  }

  private stripVisualPresentation(visual: HTMLDivElement): void {
    visual.classList.remove(TOAST_VISIBLE_CLASS);
    Object.values(POSITION_CLASSES).forEach((cls) =>
      visual.classList.remove(cls)
    );
  }

  private ensureVisualHost(): HTMLDivElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const existing = document.getElementById(TOAST_VISUAL_HOST_ID);
    if (existing instanceof HTMLDivElement) {
      return existing;
    }

    const el = document.createElement('div');
    el.id = TOAST_VISUAL_HOST_ID;
    el.setAttribute('aria-hidden', 'true');
    el.className = 'toast-visual';
    document.body.appendChild(el);
    return el;
  }

  private ensureLiveRegion(
    politeness: 'polite' | 'assertive'
  ): HTMLDivElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const id =
      politeness === 'assertive'
        ? TOAST_LIVE_REGION_ASSERTIVE_ID
        : TOAST_LIVE_REGION_POLITE_ID;

    const existing = document.getElementById(id);
    if (existing instanceof HTMLDivElement) {
      return existing;
    }

    const el = document.createElement('div');
    el.id = id;
    el.setAttribute('role', politeness === 'assertive' ? 'alert' : 'status');
    el.setAttribute('aria-live', politeness);
    el.setAttribute('aria-atomic', 'true');
    el.className = `toast-announcer toast-announcer--${politeness}`;
    document.body.appendChild(el);
    return el;
  }

  private clearVisualToast(): void {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }

    if (this.visualToastHost) {
      this.stripVisualPresentation(this.visualToastHost);
      this.visualToastHost.textContent = '';
      this.visualToastHost = null;
    }
  }
}
